import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sidebar } from '@/components/Sidebar';
import { 
  Save, 
  Download, 
  ArrowLeft, 
  Settings,
  FileDown
} from 'lucide-react';
// import PropertyPickerDialog from '@/components/PropertyPickerDialog';
import Template1 from '@/components/templates/Template1';
import Template2 from '@/components/templates/Template2';
import Template3 from '@/components/templates/Template3';
import Template4 from '@/components/templates/Template4';
import Template5 from '@/components/templates/Template5';
import Template6 from '@/components/templates/Template6';
import Template7 from '@/components/templates/Template7';
import Template8 from '@/components/templates/Template8';
import { useToast } from '@/hooks/use-toast';

interface Property {
  id: string;
  address: string;
  price: string;
  bedrooms: number;
  bathrooms: number;
  sqft: number;
  type: string;
  status: string;
  image?: string;
}

const FlyerEditor = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPropertyPicker, setShowPropertyPicker] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);

  const templateComponents = {
    '1': Template1,
    '2': Template2,
    '3': Template3,
    '4': Template4,
    '5': Template5,
    '6': Template6,
    '7': Template7,
    '8': Template8,
  };

  const TemplateComponent = templateComponents[templateId as keyof typeof templateComponents];

  const handlePropertySelect = (property: Property | null) => {
    setSelectedProperty(property);
    setShowPropertyPicker(false);
  };

  const handleSave = () => {
    toast({
      title: "Flyer Saved",
      description: "Your flyer has been saved to your account.",
    });
  };

  const handleExport = () => {
    toast({
      title: "Exporting Flyer",
      description: "Your flyer HTML is being prepared for email launches.",
    });
  };

  if (!TemplateComponent) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <main className="flex-1 p-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-primary mb-4">Template Not Found</h1>
            <Button onClick={() => navigate('/templates')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Templates
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <main className="flex-1">
        {/* Header Controls */}
        <div className="bg-white border-b border-border p-4">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                onClick={() => navigate('/templates')}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Templates
              </Button>
              <h1 className="text-xl font-semibold text-primary">
                Editing Template {templateId}
              </h1>
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowPropertyPicker(true)}
              >
                <Settings className="mr-2 h-4 w-4" />
                Property Data
              </Button>
              <Button variant="professional" onClick={handleSave}>
                <Save className="mr-2 h-4 w-4" />
                Save Flyer
              </Button>
              <Button variant="cta" onClick={handleExport}>
                <FileDown className="mr-2 h-4 w-4" />
                Export for Email
              </Button>
            </div>
          </div>
        </div>

        {/* Flyer Editor Area */}
        <div className="p-8 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <TemplateComponent property={selectedProperty} />
          </div>
        </div>

        {/* Property Picker Dialog */}
        {/* <PropertyPickerDialog
          open={showPropertyPicker}
          onClose={() => setShowPropertyPicker(false)}
          onSelectProperty={handlePropertySelect}
        /> */}
      </main>
    </div>
  );
};

export default FlyerEditor;